/**
 * Dashboard API client with CSRF token handling
 */

import type { Dashboard, SaveResponse, PanelDataResponse, AvailableField, EntitySummary } from '@/types/dashboard';

let csrfToken: string | null = null;

async function fetchApi<T>(
  url: string,
  options: RequestInit = {}
): Promise<T> {
  if (options.method && options.method !== 'GET') {
    if (!csrfToken) {
      try {
        const tokenResponse = await globalThis.fetch('/session/token');
        if (!tokenResponse.ok) {
          csrfToken = null;
          throw new Error(`Failed to fetch CSRF token (HTTP ${tokenResponse.status})`);
        }
        csrfToken = await tokenResponse.text();
      } catch (e) {
        csrfToken = null;
        if (e instanceof Error && e.message.includes('CSRF token')) {
          throw e;
        }
        throw new Error('Failed to fetch CSRF token');
      }
    }
  }

  const headers: Record<string, string> = {
    ...(options.headers as Record<string, string> || {}),
  };

  if (options.method && options.method !== 'GET') {
    headers['Content-Type'] = 'application/json';
    if (csrfToken) {
      headers['X-CSRF-Token'] = csrfToken;
    }
  }

  const response = await globalThis.fetch(url, {
    ...options,
    headers,
  });

  if (!response.ok) {
    let errorMessage = response.statusText;
    try {
      const errorData = await response.json() as { error?: string; message?: string };
      if (errorData.error) {
        errorMessage = errorData.error;
      } else if (errorData.message) {
        errorMessage = errorData.message;
      }
    } catch {
      // errorMessage already set to statusText
    }
    if (!errorMessage) {
      errorMessage = `Status ${response.status}`;
    }
    throw new Error(`HTTP ${response.status}: ${errorMessage}`);
  }

  if (response.status === 204) {
    return undefined as T;
  }
  return response.json() as Promise<T>;
}

export async function fetchConfig(sourceKey: string): Promise<Dashboard> {
  return fetchApi<Dashboard>(`/api/dashboard/config/${sourceKey}`, { method: 'GET' });
}

export async function saveConfig(dashboard: Dashboard): Promise<SaveResponse> {
  return fetchApi<SaveResponse>('/api/dashboard/save', {
    method: 'PUT',
    body: JSON.stringify(dashboard),
  });
}

export async function loadPanelData(params: {
  dashboardUuid: string;
  panelId: string;
  contextKey: string;
  panelType?: string;
  panelConfig?: Record<string, unknown>;
  formulas?: Record<string, string>;
}): Promise<PanelDataResponse> {
  return fetchApi<PanelDataResponse>('/api/dashboard/panel/data', {
    method: 'POST',
    body: JSON.stringify({
      mode: 'normal',
      dashboardUuid: params.dashboardUuid,
      panelId: params.panelId,
      contextKey: params.contextKey,
      panelType: params.panelType ?? '',
      panelConfig: params.panelConfig ?? {},
      formulas: params.formulas ?? {},
    }),
  });
}

export async function previewPanelData(params: {
  contextKey: string;
  panelType: string;
  formulas: Record<string, string>;
  panelConfig?: Record<string, unknown>;
}): Promise<PanelDataResponse> {
  return fetchApi<PanelDataResponse>('/api/dashboard/panel/data', {
    method: 'POST',
    body: JSON.stringify({
      mode: 'preview',
      contextKey: params.contextKey,
      panelType: params.panelType,
      panelConfig: params.panelConfig ?? {},
      formulas: params.formulas,
    }),
  });
}

export async function fetchAvailableFields(sourceKey: string): Promise<AvailableField[]> {
  return fetchApi<AvailableField[]>(`/api/dashboard/available-fields?sourceKey=${sourceKey}`, { method: 'GET' });
}

export async function fetchDashboard(uuid: string): Promise<Dashboard> {
  return fetchApi<Dashboard>(`/api/dashboard/${uuid}`, { method: 'GET' });
}

export async function deleteDashboard(uuid: string): Promise<void> {
  await fetchApi<void>(`/api/dashboard/${uuid}`, { method: 'DELETE' });
}

export async function setHomePage(uuid: string): Promise<{ success: boolean; uuid: string; frontPage: string }> {
  return fetchApi<{ success: boolean; uuid: string; frontPage: string }>(
    `/api/dashboard/${uuid}/set-home-page`,
    { method: 'POST' }
  );
}

export async function createDashboard(params: {
  title: string;
  viewMode?: 'full' | 'list';
  sourceKey?: string;
}): Promise<{ uuid: string; sourceKey: string }> {
  return fetchApi<{ uuid: string; sourceKey: string }>('/api/dashboard/create', {
    method: 'POST',
    body: JSON.stringify({
      title: params.title || 'New Dashboard',
      ...(params.viewMode && params.viewMode !== 'full' ? { viewMode: params.viewMode } : {}),
      ...(params.sourceKey ? { sourceKey: params.sourceKey } : {}),
    }),
  });
}

export async function fetchEntities(entityType: string, bundle: string): Promise<EntitySummary[]> {
  return fetchApi<EntitySummary[]>(`/api/entities/${entityType}/${bundle}`, { method: 'GET' });
}

export async function checkCondition(formula: string, entityUuid: string): Promise<{ result: boolean }> {
  return fetchApi<{ result: boolean }>('/api/dashboard/check-condition', {
    method: 'POST',
    body: JSON.stringify({ formula, entityUuid }),
  });
}

export { fetchApi };
